import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { DayPicker } from "react-day-picker";
import { ko } from "date-fns/locale";

import SectionTitle from "../../../../assets/components/TitleGroup";
import add from "../../../../assets/icons/System/add.svg";
import remove from "../../../../assets/icons/System/remove.svg";
import add_dis from "../../../../assets/icons/System/add_disabled.svg";
import remove_dis from "../../../../assets/icons/System/remove_disabled.svg";
import down from "../../../../assets/icons/Arrow/down_tri.svg"
import { SimpleButton, SplitRowQuarter } from "../../../../assets/components/ButtonGroup";
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup";
import info from "../../../../assets/icons/warning/info_red.svg"
import TimeSelect from "../../../../assets/components/TimeSelect";

const WEEK_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

// 같은 날인지 비교
const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// 시간 제거(00:00:00으로 맞추기)
const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// 1~31일 버튼용
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// 기간 표시용 포맷터 (2025.11.12)
const formatDotDate = (date) => {
  if (!date) return "-";
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}.${m}.${d}`;
};

// "HH:MM" 이 지금 시각보다 과거인지 여부
const isPastTimeToday = (value) => {
  if (!value) return false;
  const [hhStr, mmStr] = value.split(":");
  const hh = Number(hhStr);
  const mm = Number(mmStr);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return false;

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const valueMinutes = hh * 60 + mm;

  return valueMinutes < nowMinutes;
};

// ──────────────────────────────────────────────
// Step2 : 기간 + 반복 + 시간
// ──────────────────────────────────────────────
const Reserve2 = ({
  repeatCycle,
  setRepeatCycle,
  repeatCount,
  setRepeatCount,
  start,
  end,
  setStart,
  setEnd,
  selectedWeekdays,
  setSelectedWeekdays,
  selectedDates,
  setSelectedDates,
  timeSelections,
  setTimeSelections,
  repeatTime,
  setRepeatTime,
  openMonthlyCalendar,
  setOpenMonthlyCalendar,
  autoScroll = true,
  useBlank = true,
  onWeeklyRangeValidChange,
  onMonthlyRangeValidChange,
  onTimeValidChange,
}) => {

  // 스크롤 자동화 : 알림 기간 / 알림 반복 / 반복 상세 영역 DOM 참조
  const periodRef = useRef(null);
  const bottomPanelRef = useRef(null);
  const repeatContentRef = useRef(null);
  const prevRepeatCycleRef = useRef(repeatCycle);


  // 기간 표시용
  const formatDot = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  };

  // 오늘(시간 제거)
  const today = stripTime(new Date());
  const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // 엘리먼트의 실제 스크롤 부모 찾기
  const getScrollParent = (node) => {
    if (!node) return null;

    let parent = node.parentElement;
    const overflowRegex = /(auto|scroll)/;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const oy = style.overflowY;
      const o = style.overflow;

      if (overflowRegex.test(oy) || overflowRegex.test(o)) {
        return parent;
      }
      parent = parent.parentElement;
    }

    // 못 찾으면 window 기준으로 처리
    return window;
  };

  const weeklyCount = selectedWeekdays.length;
  const monthlyCount = selectedDates.length;

  // 알림 기간이 "오늘 하루(시작일 = 종료일 = 오늘)" 인지 여부
  const isPeriodTodayOnly = useMemo(() => {
    if (!start || !end) return false;
    const baseToday = stripTime(new Date());
    return (
      isSameDay(stripTime(start), baseToday) &&
      isSameDay(stripTime(end), baseToday)
    );
  }, [start, end]);

  const [timeErrors, setTimeErrors] = useState([]);
  const [repeatTimeError, setRepeatTimeError] = useState(false); // weekly/monthly용 단일 에러

  // 주간 반복 요일 경고 상태
  const [weeklyWarning, setWeeklyWarning] = useState(false);        // 경고 문구 노출 여부
  const [weeklyConflictDays, setWeeklyConflictDays] = useState([]); // 기간 밖으로 밀려난 요일들
  const prevRangeRef = useRef({ start, end });                       // 이전 알림 기간 기억


  // DayPicker가 쓸 range 상태 (from/to)
  const [range, setRange] = useState(() => {
    return {
      from: start ?? undefined,
      to: end ?? undefined,
    };
  });


  // 최소 한 번이라도 start/end를 모두 선택했는지 여부
  const [hasRangeEverCompleted, setHasRangeEverCompleted] = useState(
    () => !!(start && end)
  );

  // 현재 "새로 구간 고르는 중인지" (시작일만 찍고 종료일은 아직 없는 상태)
  const isRangeSelecting =
    hasRangeEverCompleted && range.from && !range.to;

  const isOnlyStartSelected = !!(range.from && !range.to);

  // from/to가 둘 다 있는지
  const hasFullRange = !!(range.from && range.to);
  // 알림 반복 블록을 보여줄지 여부
  const showBottomPanel = hasRangeEverCompleted || hasFullRange;

  // 시작+종료일이 모두 선택된 순간 알림 반복 영역으로 스크롤
  useEffect(() => {
    if (!autoScroll) return;
    if (!hasFullRange || !bottomPanelRef.current) return;

    const el = bottomPanelRef.current;
    const scrollEl = getScrollParent(el);

    const rect = el.getBoundingClientRect();

    // 스크롤 대상이 window 인 경우
    if (scrollEl === window) {
      const targetTop = window.scrollY + rect.top - 16;

      window.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
    } else {
      // 스크롤 대상이 div 같은 컨테이너인 경우
      const parentRect = scrollEl.getBoundingClientRect();
      const targetTop =
        scrollEl.scrollTop + (rect.top - parentRect.top) - 16;

      scrollEl.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
    }
  }, [hasFullRange, autoScroll]);


  // 반복 탭(daily/weekly/monthly) 선택 시 아래 반복 영역으로 스크롤
  useEffect(() => {
    if (!autoScroll) return;
    if (!hasFullRange) return;
    if (!repeatCycle) return;
    if (!repeatContentRef.current) return;

    const el = repeatContentRef.current;
    const scrollEl = getScrollParent(el);
    const rect = el.getBoundingClientRect();

    if (scrollEl === window) {
      const targetTop = window.scrollY + rect.top - 16;
      window.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
    } else {
      const parentRect = scrollEl.getBoundingClientRect();
      const targetTop =
        scrollEl.scrollTop + (rect.top - parentRect.top) - 16;

      scrollEl.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
    }
  }, [repeatCycle, hasFullRange, autoScroll]);


  // 반복 주기 선택이 끝났을 때(선택된 탭이 해제될 때) 알림 기간으로 스크롤
  useEffect(() => {
    const prev = prevRepeatCycleRef.current;

    // 이전에는 탭이 하나라도 선택되어 있었는데, 지금은 해제된 상태
    if (autoScroll && hasFullRange && prev && !repeatCycle && bottomPanelRef.current) {
      bottomPanelRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    prevRepeatCycleRef.current = repeatCycle;
  }, [repeatCycle, hasFullRange, autoScroll]);


  useEffect(() => {
    // 알림 기간이 오늘 하루가 아닌 경우: 과거 시간 제한 없음 → 에러 초기화
    if (!isPeriodTodayOnly) {
      setTimeErrors([]);
      setRepeatTimeError(false);
      return;
    }

    // 오늘 하루일 때: 이미 선택된 시간들 다시 검사

    if (repeatCycle === "daily") {
      // daily: 여러 개 시간 배열 검사
      const nextErrors = Array.from({ length: repeatCount }, (_, i) => {
        const val = timeSelections[i];
        return !!(val && isPastTimeToday(val));
      });
      setTimeErrors(nextErrors);
      setRepeatTimeError(false); // daily에서는 단일 시간 에러는 사용 X
    } else if (repeatCycle === "weekly" || repeatCycle === "monthly") {
      // weekly / monthly: 단일 repeatTime만 검사
      const isPast = !!(repeatTime && isPastTimeToday(repeatTime));
      setRepeatTimeError(isPast);
      setTimeErrors([]); // 안전하게 초기화
    } else {
      // 탭 선택 안 된 상태
      setTimeErrors([]);
      setRepeatTimeError(false);
    }
  }, [
    isPeriodTodayOnly,
    repeatCycle,
    repeatCount,
    timeSelections,
    repeatTime,
  ]);


  // DayPicker month 상태 (화살표 이동용)
  const [month, setMonth] = useState(() => {
    const base = start ?? today;
    // base가 이번 달보다 이전이면 무조건 이번 달로
    return base < minMonth ? minMonth : base;
  });

  // DayPicker 클릭 규칙 (TestPeriod와 동일)
  const handleDayClick = useCallback(
    (day) => {
      const clicked = stripTime(day);
      const { from, to } = range;
      const fromDate = from ? stripTime(from) : undefined;

      // 오늘 이전 날짜는 전체 선택 불가
      if (clicked < today) return;

      // 1) 아무것도 선택 안 된 상태 → 첫 클릭 = 시작일
      if (!from && !to) {
        setRange({ from: clicked, to: undefined });
        return;
      }

      // 2) 시작일만 있고 종료일 없는 상태 (두 번째 클릭)
      if (from && !to) {
        // 같은 날짜 재클릭 → 당일 1일 기간
        if (fromDate && isSameDay(clicked, fromDate)) {
          setRange({ from: fromDate, to: clicked });
          return;
        }

        if (fromDate) {
          if (clicked >= fromDate) {
            // 시작일보다 같거나 이후 → 그대로 종료일
            setRange({ from: fromDate, to: clicked });
          } else {
            // 시작일보다 이전 → 서로 바꿔서 from/to 구성
            setRange({ from: clicked, to: fromDate });
          }
        }
        return;
      }

      // 3) 시작/종료가 모두 있는 상태 → 항상 새 시작일로 리셋
      setRange({ from: clicked, to: undefined });
    },
    [range, today]
  );

  // range가 바뀔 때마다 상위 start/end 동기화
  useEffect(() => {
    if (!range.from) {
      setStart(null);
      setEnd(null);
      return;
    }
    setStart(range.from);
    setEnd(range.to ?? null);
  }, [range.from, range.to, setStart, setEnd]);

  // from/to가 둘 다 들어온 순간, "한번이라도 완료한 적 있다" 표시
  useEffect(() => {
    if (range.from && range.to) {
      setHasRangeEverCompleted(true);
    }
  }, [range.from, range.to]);

  // DayPicker 하이라이트용 modifiers (TestPeriod와 동일 패턴)
  const modifiers = {
    selectedRange:
      range.from && range.to ? { from: range.from, to: range.to } : undefined,
    rangeStart: range.from,
    rangeEnd: range.to,

    // 기간 안 + 일요일
    rangeSunday: (day) => {
      if (!range.from || !range.to) return false;
      const s = stripTime(range.from);
      const e = stripTime(range.to);
      const d = stripTime(day);
      return d >= s && d <= e && d.getDay() === 0;
    },

    // 기간 안 + 토요일
    rangeSaturday: (day) => {
      if (!range.from || !range.to) return false;
      const s = stripTime(range.from);
      const e = stripTime(range.to);
      const d = stripTime(day);
      return d >= s && d <= e && d.getDay() === 6;
    },

    // 기간 안 + 해당 월의 1일
    rangeMonthFirst: (day) => {
      if (!range.from || !range.to) return false;
      const s = stripTime(range.from);
      const e = stripTime(range.to);
      const d = stripTime(day);
      return d >= s && d <= e && d.getDate() === 1;
    },

    // 기간 안 + 해당 월의 말일
    rangeMonthLast: (day) => {
      if (!range.from || !range.to) return false;
      const s = stripTime(range.from);
      const e = stripTime(range.to);
      const d = stripTime(day);
      const lastDateOfMonth = new Date(
        d.getFullYear(),
        d.getMonth() + 1,
        0
      ).getDate();
      return d >= s && d <= e && d.getDate() === lastDateOfMonth;
    },
  };


  // start~end 사이 7일 이내라면, 그 구간에 포함되는 요일만 허용
  const allowedWeekdays = useMemo(() => {
    if (!start || !end) return null;

    let s = stripTime(start);
    let e = stripTime(end);
    if (s > e) {
      const tmp = s;
      s = e;
      e = tmp;
    }

    const diffDays = Math.round(
      (e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)
    );

    // 7일 초과면 요일 제한 X
    if (diffDays > 6) return null;

    const set = new Set();
    const cur = new Date(s);

    while (cur <= e) {
      const weekdayIndex = cur.getDay(); // 0(일)~6(토)
      set.add(WEEK_LABELS[weekdayIndex]); // "일","월",...,"토"
      cur.setDate(cur.getDate() + 1);
    }

    return set; // 예: {"화","수"}
  }, [start, end]);

  // 알림 기간을 다시 선택했을 때 요일과의 충돌 검사
  useEffect(() => {
    const prev = prevRangeRef.current;
    const prevStart = prev.start;
    const prevEnd = prev.end;

    const changed =
      (!!prevStart !== !!start) ||
      (!!prevEnd !== !!end) ||
      (prevStart && start && prevStart.getTime() !== start.getTime()) ||
      (prevEnd && end && prevEnd.getTime() !== end.getTime());

    if (!changed) return;

    // 현재 기간을 다음 비교를 위한 기준으로 저장
    prevRangeRef.current = { start, end };

    // 기간이 아직 완성되지 않았으면(시작/종료 둘 다 있어야) 검사 안 함
    if (!start || !end) {
      setWeeklyWarning(false);
      setWeeklyConflictDays([]);
      onWeeklyRangeValidChange && onWeeklyRangeValidChange(true);
      return;
    }

    // weekly가 아닌 탭일 땐 경고 상태 초기화만
    if (repeatCycle !== "weekly") {
      setWeeklyWarning(false);
      setWeeklyConflictDays([]);
      onWeeklyRangeValidChange && onWeeklyRangeValidChange(true);
      return;
    }

    // [규칙 2] 이미 경고 상태에서 기간을 한 번 더 바꾸면 → 무조건 초기화
    if (weeklyWarning) {
      setWeeklyWarning(false);
      setWeeklyConflictDays([]);
      setSelectedWeekdays([]);
      onWeeklyRangeValidChange && onWeeklyRangeValidChange(true);
      return;
    }

    // 이전에 선택해 둔 요일이 없으면 검사 의미 없음
    if (!selectedWeekdays || selectedWeekdays.length === 0) {
      onWeeklyRangeValidChange && onWeeklyRangeValidChange(true);
      return;
    }

    // 이번 기간에 포함되는 요일 집합
    const periodWeekdays =
      allowedWeekdays ?? new Set(WEEK_LABELS); // 7일 이상이면 모든 요일이 포함된다고 간주

    // 이번 기간에 포함되지 않는(=기간 밖으로 밀려난) 기존 선택 요일
    const invalid = selectedWeekdays.filter(
      (label) => !periodWeekdays.has(label)
    );

    if (invalid.length > 0) {
      // [규칙 1] 일부 요일이 새 기간에 포함되지 않음 → 경고 + 다음 버튼 비활성화
      setWeeklyWarning(true);
      setWeeklyConflictDays(invalid);

      // "포함된 요일은 초기화" → 기존 선택은 전부 해제
      setSelectedWeekdays([]);

      onWeeklyRangeValidChange && onWeeklyRangeValidChange(false);
    } else {
      // 전부 새 기간에 포함됨 → 경고 해제, 그대로 사용 가능
      setWeeklyWarning(false);
      setWeeklyConflictDays([]);
      onWeeklyRangeValidChange && onWeeklyRangeValidChange(true);
    }
  }, [
    start,
    end,
    repeatCycle,
    selectedWeekdays,
    weeklyWarning,
    allowedWeekdays,
    onWeeklyRangeValidChange,
    setSelectedWeekdays,
  ]);



  const allowedDays = useMemo(() => {
    if (!start || !end) return null;

    let s = stripTime(start);
    let e = stripTime(end);
    if (s > e) {
      const tmp = s;
      s = e;
      e = tmp;
    }

    const result = new Set();

    let year = s.getFullYear();
    let month = s.getMonth();
    const endYear = e.getFullYear();
    const endMonth = e.getMonth();

    while (year < endYear || (year === endYear && month <= endMonth)) {
      const isStartMonth = year === s.getFullYear() && month === s.getMonth();
      const isEndMonth = year === e.getFullYear() && month === e.getMonth();

      const firstDay = isStartMonth ? s.getDate() : 1;
      const lastDay = isEndMonth
        ? e.getDate()
        : new Date(year, month + 1, 0).getDate(); // 그 달의 말일

      for (let d = firstDay; d <= lastDay; d += 1) {
        result.add(d);
      }

      // 다음 달로 이동
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }

    return result;
  }, [start, end]);

  const isDayEnabled = (day) => {
    // start/end 없으면 일단 모두 허용
    if (!allowedDays) return true;
    return allowedDays.has(day);
  };

  // 새로 설정한 기간(start~end) 밖의 날짜가 선택돼 있는지 체크
  const invalidMonthlySelection = useMemo(() => {
    if (!selectedDates || selectedDates.length === 0) return false;
    if (!allowedDays) return false; // 아직 기간이 없으면 경고 안 띄움

    return selectedDates.some((dayStr) => {
      const dayNum = Number(dayStr);
      return !allowedDays.has(dayNum); // 허용되지 않는 dayNum 이 있으면 true
    });
  }, [selectedDates, allowedDays]);




  // monthly: 현재 선택된 날짜들이 "알림 기간"과 일치하는지 여부를 부모에게 전달
  useEffect(() => {
    if (!onMonthlyRangeValidChange) return;

    // 기간이 없거나, monthly 탭이 아니면 → true로 보고, 최종 판단은 hasStep2가 함
    if (!start || !end || repeatCycle !== "monthly") {
      onMonthlyRangeValidChange(true);
      return;
    }

    // Reserve2 내부에서 invalidMonthlySelection으로 잡힌 상태면 false
    onMonthlyRangeValidChange(!invalidMonthlySelection);
  }, [
    onMonthlyRangeValidChange,
    start,
    end,
    repeatCycle,
    invalidMonthlySelection,
  ]);


  const MAX_MONTHLY_SELECTION = 7;
  const maxMonthlyCount = MAX_MONTHLY_SELECTION;


  // 알림 시간 섹션이 한 번이라도 화면에 등장했는지
  const [hasTimeAreaEverShown, setHasTimeAreaEverShown] = useState(false);

  // 탭 변경 시 값만 초기화 (섹션은 플래그로 유지)
  const resetOnTabChange = useCallback(() => {
    setRepeatCount(0);        // daily 회차
    setSelectedWeekdays([]);  // weekly 요일
    setSelectedDates([]);     // monthly 날짜
    setTimeSelections([]);    // daily 시간 배열
    setRepeatTime("");        // weekly/monthly 시간
    setWeeklyWarning(false);  // 주간 경고 상태 초기화
    setWeeklyConflictDays([]);
    setTimeErrors([]);
    setRepeatTimeError(false);
    // 상위 hasStep2 에서 "다음" 버튼 다시 활성화
    onWeeklyRangeValidChange && onWeeklyRangeValidChange(true);
  }, [
    setRepeatCount,
    setSelectedWeekdays,
    setSelectedDates,
    setTimeSelections,
    setRepeatTime,
    onWeeklyRangeValidChange,
  ]);

  // 요일/날짜 선택으로 처음 알림 시간 섹션이 등장하는 경우도 플래그 켜기
  useEffect(() => {
    if (hasTimeAreaEverShown) return;

    if (
      (repeatCycle === "weekly" && weeklyCount > 0) ||
      (repeatCycle === "monthly" && monthlyCount > 0)
    ) {
      setHasTimeAreaEverShown(true);
    }
  }, [repeatCycle, weeklyCount, monthlyCount, hasTimeAreaEverShown]);

  const handleTimeSelectChange = useCallback(
    (index, newValue) => {
      // 알림 기간이 오늘 하루일 때만 과거 시간 검사
      const past = isPeriodTodayOnly ? isPastTimeToday(newValue) : false;

      // 에러 플래그 갱신
      setTimeErrors((prev) => {
        const next = [...prev];
        next[index] = past;
        return next;
      });

      // 과거 시간이든 아니든 값은 항상 저장 (바텀시트에서 "저장"은 항상 동작)
      setTimeSelections((prev) => {
        const next = [...prev];
        next[index] = newValue;
        return next;
      });
    },
    [setTimeSelections, isPeriodTodayOnly]
  );

  // weekly / monthly 공용 단일 시간 변경 핸들러
  const handleRepeatTimeChange = useCallback(
    (newValue) => {
      // 알림 기간이 오늘 하루일 때만 과거 시간 검사
      const past = isPeriodTodayOnly ? isPastTimeToday(newValue) : false;

      setRepeatTimeError(past);

      // 과거 시간이더라도 값은 항상 저장
      setRepeatTime(newValue);
    },
    [setRepeatTime, isPeriodTodayOnly]
  );

  // Step2 시간 설정 전체 유효성 부모에게 전달
  useEffect(() => {
    if (!onTimeValidChange) return;

    let valid = true;

    // 아직 탭을 안 골랐으면 시간 세팅이 안 된 상태라고 보고 false
    if (!repeatCycle) {
      valid = false;
    } else if (repeatCycle === "daily") {
      // 회차가 0이거나, 하나라도 시간 비어있거나 에러 있으면 false
      if (repeatCount === 0) {
        valid = false;
      } else {
        for (let i = 0; i < repeatCount; i += 1) {
          if (!timeSelections[i] || timeErrors[i]) {
            valid = false;
            break;
          }
        }
      }
    } else if (repeatCycle === "weekly" || repeatCycle === "monthly") {
      // weekly / monthly 는 단일 시간 + 에러 플래그 기준
      if (!repeatTime || repeatTimeError) {
        valid = false;
      }
    }

    onTimeValidChange(valid);
  }, [
    repeatCycle,
    repeatCount,
    timeSelections,
    timeErrors,
    repeatTime,
    repeatTimeError,
    onTimeValidChange,
  ]);

  return (
    <div className="">
      {/* 알림 기간 */}
      <section className="p-[16px]" ref={periodRef}>
        <h2 className="text-t3 text-text-default pb-[8px]">알림 기간</h2>
        <p className="text-b2 text-text-subtler pb-[24px]">
          선택한 기간에만 전송돼요. 시작 - 종료 날짜를 선택하세요.
        </p>

        {/* 캘린더 */}
        <div className={`w-full rounded-[20px] border border-border-default p-[8px] flex justify-center ${isOnlyStartSelected ? "only-start" : ""}`}>
          <DayPicker
            mode="default"
            locale={ko}
            month={month}
            onMonthChange={(m) => {
              if (m < minMonth) {
                setMonth(minMonth);
              } else {
                setMonth(m);
              }
            }}
            fromMonth={minMonth}
            selected={undefined}
            disabled={{ before: today }}
            onDayClick={handleDayClick}
            numberOfMonths={1}
            weekStartsOn={0}
            modifiers={modifiers}
            modifiersClassNames={{
              selectedRange: "rdp-selected-range",
              rangeStart: "rdp-range-start",
              rangeEnd: "rdp-range-end",
              rangeSunday: "rdp-range-sunday",
              rangeSaturday: "rdp-range-saturday",
              rangeMonthFirst: "rdp-range-month-first",
              rangeMonthLast: "rdp-range-month-last",
            }}
            navLayout="around"
            initialFocus={false}
            formatters={{
              formatCaption: (month) =>
                `${month.getFullYear()}년 ${month.getMonth() + 1}월`,
            }}
          />
        </div>

        {/* 선택된 날짜 요약 */}
        <div className="pt-[16px]">
          {/* <div className="flex justify-between items-center py-[12px] px-[16px] border border-border-default rounded-[16px]">
            <div className="flex-1 text-center">
              <div className="text-text-subtler pb-[4px] text-b2">시작일</div>
              {start ? (
                <div className="text-text-primary text-t4">
                  {formatDot(start)}
                </div>
              ) : (
                <p className="text-t4 text-text-subtler">선택해 주세요</p>
              )}
            </div>
            <div className="text-t4 text-text-subtle">~</div>
            <div className="flex-1 text-center">
              <div className="text-text-subtler pb-[4px] text-b2">종료일</div>
              {end ? (
                <div className="text-text-primary text-t4">
                  {formatDot(end)}
                </div>
              ) : (
                <p className="text-t4 text-text-subtler">선택해 주세요</p>
              )}
            </div>
          </div> */}
          <div className="py-[12px] px-[16px] border border-border-default rounded-[16px]">
            <div className="flex justify-between items-center pb-[4px]">
              <div className="flex-1 text-center text-text-subtler text-b2">
                시작일
              </div>
              <div className="w-[24px]" />
              <div className="flex-1 text-center text-text-subtler text-b2">
                종료일
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex-1 text-center">
                {start ? (
                  <div className="text-text-primary text-t4">
                    {formatDot(start)}
                  </div>
                ) : (
                  <p className="text-t4 text-text-subtler">선택해 주세요</p>
                )}
              </div>

              <div className="px-[8px] text-t4 text-text-subtle">~</div>

              <div className="flex-1 text-center">
                {end ? (
                  <div className="text-text-primary text-t4">
                    {formatDot(end)}
                  </div>
                ) : (
                  <p className="text-t4 text-text-subtler">선택해 주세요</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {showBottomPanel && (
        <div ref={bottomPanelRef}>
          <div className="py-[16px]">
            <div className="bg-bg-white-subtle h-[16px]" />
          </div>

          {/* 여기부터가 dimmed 대상: 알림 반복 ~ 가장 아래 내용까지 */}
          <div className="relative">
            {/* 실제 내용: 선택 중에는 클릭 막기 */}
            <div className={isRangeSelecting ? "pointer-events-none" : ""}>
              {/* 알림 반복 */}
              <section ref={repeatContentRef}>
                <div className="p-[16px]">
                  <h2 className="text-t3 text-text-default pb-[8px]">알림 반복</h2>
                  <p className="text-b2 text-text-subtler pb-[24px]">
                    선택한 항목으로 알림이 반복돼요.
                  </p>

                  {/* 시간 / 요일 / 날짜 탭 - SimpleButton 사용 */}
                  <div className="flex gap-[8px]">
                    {[
                      { key: "daily", label: "시간" },
                      { key: "weekly", label: "요일" },
                      { key: "monthly", label: "날짜" },
                    ].map((tab) => {
                      const isSelected = repeatCycle === tab.key;
                      return (
                        <SimpleButton
                          label={tab.label}
                          key={tab.key}
                          type={isSelected ? "select" : "unselect"}
                          onClick={() => {
                            // 같은 탭을 다시 클릭해도 해제되지 않음
                            if (repeatCycle === tab.key) {
                              return;
                            }

                            // 다른 탭으로 이동할 때마다 값들 리셋
                            resetOnTabChange();

                            // 탭 변경
                            setRepeatCycle(tab.key);

                            // 시간(daily) 탭은 들어가는 순간 반복횟수 기본 1 + 알림시간 섹션 발생
                            if (tab.key === "daily") {
                              setRepeatCount(1);
                              if (!hasTimeAreaEverShown) {
                                setHasTimeAreaEverShown(true);
                              }
                            }
                          }}
                          className="flex-1"
                        />
                      );
                    })}
                  </div>
                </div>

                {/* weekly/monthly/daily */}
                {repeatCycle ? (
                  <>
                    {/* 시간 탭 선택 시 */}
                    {repeatCycle === "daily" && (
                      <>
                        {/* 반복 횟수 */}
                        <div className="p-[16px] pt-[8px]">
                          <div className="flex items-center justify-between p-[16px] rounded-[16px] bg-bg-white-subtle">
                            <div className="text-text-default text-b1 flex gap-1">
                              반복 횟수
                              <span className="text-text-subtler">(최대 7개)</span>
                            </div>

                            {/** 반복 횟수 버튼 영역 */}
                            <div className="flex items-center gap-[8px]">
                              {(() => {
                                const canDecrement = repeatCount > 1;
                                const canIncrement = repeatCount < 7;

                                return (
                                  <>
                                    {/* - 버튼 */}
                                    <button
                                      type="button"
                                      disabled={!canDecrement}
                                      className={[
                                        "w-[24px] h-[24px] rounded-full flex items-center justify-center text-b2",
                                        canDecrement
                                          ? "bg-btn-secondary-fill"
                                          : "bg-btn-disabled-fill cursor-not-allowed",
                                      ].join(" ")}
                                      onClick={() => {
                                        if (!canDecrement) return;
                                        setRepeatCount((c) => (c > 1 ? c - 1 : 1));
                                      }}
                                    >
                                      <img src={!canDecrement ? remove_dis : remove} alt="remove" />
                                    </button>

                                    <span className="w-[16px] text-center text-t4 text-text-primary">
                                      {repeatCount}
                                    </span>

                                    {/* + 버튼 */}
                                    <button
                                      type="button"
                                      disabled={!canIncrement}
                                      className={[
                                        "w-[24px] h-[24px] rounded-full flex items-center justify-center text-b2",
                                        canIncrement
                                          ? "bg-btn-secondary-fill"
                                          : "bg-btn-disabled-fill cursor-not-allowed",
                                      ].join(" ")}
                                      onClick={() => {
                                        if (!canIncrement) return;
                                        setRepeatCount((c) => (c < 7 ? c + 1 : c));
                                      }}
                                    >
                                      <img src={!canIncrement ? add_dis : add} alt="add" />
                                    </button>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* 구분선 */}
                        <div className="py-[16px]">
                          <div className="bg-bg-white-subtle h-[16px]" />
                        </div>

                        {(repeatCount > 0 || hasTimeAreaEverShown) && (
                          <div
                            className={
                              `p-[16px] space-y-[20px] ` +
                              (useBlank ? "min-h-[282px]" : "")
                            }
                          >
                            <SectionTitle
                              title="알림 시간"
                              subTitle="선택한 시간에 알림을 보내드려요."
                              className="pb-[4px]"
                            />
                            {Array.from({ length: repeatCount }, (_, idx) => (
                              <div
                                key={`time-${idx}`}
                                className="flex items-start gap-[16px]"  // items-center → items-start
                              >
                                <span className="w-[40px] pt-[8px] text-b1 text-text-subtle text-center">
                                  {/* 살짝 위 여백 줘서 타임셀렉트와 수평 맞추기 */}
                                  {idx + 1}회
                                </span>

                                <div className="flex-1">
                                  <TimeSelect
                                    value={timeSelections[idx] || ""}
                                    onChange={(newValue) => {
                                      handleTimeSelectChange(idx, newValue);
                                    }}
                                    hasError={timeErrors[idx]}
                                  />

                                  {timeErrors[idx] && (
                                    <div className="flex items-start gap-[4px] pt-[8px]">
                                      <img src={info} alt="에러" className="w-[16px] h-[16px]" />
                                      <p className="text-c2 text-text-danger">
                                        현재 시각보다 이전 시각은 선택할 수 없습니다.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* 요일 탭 선택 시 */}
                    {repeatCycle === "weekly" && (
                      <>
                        {/* 반복 요일 */}
                        <div className="p-[16px] pt-[8px]">
                          <div className="items-center  p-[16px] rounded-[16px] bg-bg-white-subtle">
                            <p className="text-b1 text-text-default pb-[12px]">
                              반복 요일
                            </p>
                            <div className="flex justify-between gap-[8px] flex-wrap">
                              {WEEK_LABELS.map((label) => {
                                const selected = selectedWeekdays.includes(label);
                                const isConflict = weeklyConflictDays.includes(label); // 기간 밖으로 밀려난 요일인지
                                const isDisabledByRange =
                                  allowedWeekdays && !allowedWeekdays.has(label);      // 7일 이하 구간에서 애초에 선택이 불가능한 요일

                                const isOutOfRange = isDisabledByRange || isConflict;

                                const handleClick = () => {
                                  // 완전히 선택 불가능한 요일(기간에 전혀 안 들어오는 요일)은 무시
                                  if (isOutOfRange) return;

                                  // 경고가 떠 있는 상태에서 새 요일 선택
                                  // → 기간 밖으로 표시되던 요일들 초기화 + 새 요일만 selected
                                  if (weeklyWarning) {
                                    setWeeklyWarning(false);
                                    setWeeklyConflictDays([]);
                                    setSelectedWeekdays([label]); // 새 선택만 유지

                                    onWeeklyRangeValidChange && onWeeklyRangeValidChange(true);
                                    return;
                                  }

                                  // 평소 토글 동작
                                  if (selected) {
                                    setSelectedWeekdays((prev) => prev.filter((v) => v !== label));
                                  } else {
                                    setSelectedWeekdays((prev) => {
                                      if (prev.length >= 7) return prev;
                                      return [...prev, label];
                                    });
                                  }
                                };

                                // 상태별 스타일
                                let stateClasses = "";
                                if (isConflict) {
                                  stateClasses =
                                    "text-c1 border-border-subtle bg-surface-light-subtle text-text-danger cursor-not-allowed";
                                } else if (isDisabledByRange) {
                                  stateClasses =
                                    "text-c2 border-border-subtle bg-surface-light-subtle text-text-disabled cursor-not-allowed";
                                } else if (selected) {
                                  stateClasses =
                                    "text-c1 border-border-primary bg-surface-light-primary-light text-text-primary";
                                } else {
                                  stateClasses =
                                    "text-c2 border-border-subtle bg-surface-light-white text-text-subtler";
                                }

                                return (
                                  <button
                                    key={label}
                                    type="button"
                                    onClick={handleClick}
                                    disabled={isOutOfRange}
                                    className={[
                                      "w-[36px] h-[36px] rounded-full border ",
                                      stateClasses,
                                    ].join(" ")}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>

                            {weeklyWarning && (
                              <div className="flex pt-[8px] gap-[4px]">
                                <img src={info} alt="warning" className="h-[16px]" />
                                <p className="text-c2 text-text-danger">
                                  알림 기간과 일치하지 않습니다. 다시 선택해 주세요.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {(hasTimeAreaEverShown || weeklyCount > 0) ? (
                          <div className={useBlank ? "h-[282px]" : ""}>
                            {/* 구분선 */}
                            <div className="py-[16px]">
                              <div className="bg-bg-white-subtle h-[16px]" />
                            </div>

                            {/* 알림 시간 (1개) */}
                            <div className="p-[16px]">
                              <SectionTitle
                                title="알림 시간"
                                subTitle="선택한 시간에 알림을 보내드려요."
                                className="pb-[24px]"
                              />

                              <div className="flex-1">
                                <TimeSelect
                                  value={repeatTime || ""}
                                  onChange={handleRepeatTimeChange}
                                  hasError={repeatTimeError}
                                />

                                {repeatTimeError && (
                                  <div className="flex items-start gap-[4px] pt-[8px]">
                                    <img src={info} alt="에러" className="w-[16px] h-[16px]" />
                                    <p className="text-c2 text-text-danger">현재 시각보다 이전 시각은 선택할 수 없습니다.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (<div className={useBlank ? "p-[141px]" : ""} />)}
                      </>
                    )}

                    {/* 날짜 탭 선택 시 */}
                    {repeatCycle === "monthly" && (
                      <>
                        {/* 반복 날짜 */}
                        <div className="p-[16px] pt-[8px]">
                          <div className="items-center  p-[16px] rounded-[16px] bg-bg-white-subtle">
                            <p className="text-b1 text-text-default pb-[12px]">
                              반복 날짜
                            </p>
                            <button
                              type="button"
                              className="w-full bg-surface-light-white rounded-[12px] p-[12px] pl-[16px] flex items-center justify-between text-b1 text-text-subtle"
                              onClick={() => {
                                if (invalidMonthlySelection) {
                                  setSelectedDates([]);
                                }
                                setOpenMonthlyCalendar(true);
                              }}
                            >
                              <div className="text-left">
                                {selectedDates.length > 0 ? (
                                  <div className={`flex flex-wrap gap-x-[4px] ${invalidMonthlySelection
                                    ? "text-text-danger"
                                    : "text-text-subtle"
                                    }`}
                                  >
                                    {selectedDates.map((dayStr, idx) => {
                                      const label = `${dayStr}일${idx === selectedDates.length - 1 ? '' : ','}`;
                                      return (
                                        <span
                                          key={`${dayStr}-${idx}`}
                                          className="inline-block"
                                        >
                                          {label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-b1 text-text-subtle">
                                    날짜를 선택해 주세요
                                  </span>
                                )}
                              </div>

                              <span className="text-c2 text-text-subtler">
                                <img src={down} alt="선택" />
                              </span>
                            </button>
                            {invalidMonthlySelection && (
                              <div className="flex pt-[8px] gap-[4px]">
                                <img src={info} alt="warning" className="h-[16px]" />
                                <p className="text-c2 text-text-danger">
                                  알림 기간과 일치하지 않습니다. 다시 선택해 주세요.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {(hasTimeAreaEverShown || monthlyCount > 0) ? (
                          <div className={useBlank ? "h-[270px]" : ""}>
                            {/* 구분선 */}
                            <div className="py-[16px]">
                              <div className="bg-bg-white-subtle h-[16px]" />
                            </div>

                            {/* 알림 시간 (1개) */}
                            <div className="p-[16px]">
                              <SectionTitle
                                title="알림 시간"
                                subTitle="선택한 시간에 알림을 보내드려요."
                                className="pb-[24px]"
                              />

                              <div className="flex-1">
                                <TimeSelect
                                  value={repeatTime || ""}
                                  onChange={handleRepeatTimeChange}
                                  hasError={repeatTimeError}
                                />

                                {repeatTimeError && (
                                  <div className="flex items-start gap-[4px] pt-[8px]">
                                    <img src={info} alt="에러" className="w-[16px] h-[16px]" />
                                    <p className="text-c2 text-text-danger">현재 시각보다 이전 시각은 선택할 수 없습니다.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (<div className={useBlank ? "p-[135px]" : ""} />)}
                      </>
                    )}
                  </>
                ) : (
                  <div className={useBlank && "p-[150px]"} />
                )}
              </section>

              {/* 날짜 탭에서 사용하는 반복 날짜 선택 바텀시트 */}
              {repeatCycle === "monthly" && (
                <BottomWithButton
                  isOpen={openMonthlyCalendar}
                  onClose={() => setOpenMonthlyCalendar(false)}
                  height="h-[520px]"
                  title="반복 날짜 선택"
                  subtitle={
                    <>
                      {`설정기간: ${formatDotDate(start)} ~ ${formatDotDate(end)}`}
                      <br />
                      설정한 기간의 날짜만 선택할 수 있어요.
                      <br />
                      <span className="text-text-danger">
                        (최대 7개 선택 가능)
                      </span>
                    </>
                  }
                  // 날짜 하나도 선택 안 했으면 저장 비활성화
                  buttonDisabled={selectedDates.length === 0}
                  onSubmit={() => {
                    // 여기서 selectedDates는 ["2","13","21"] 처럼 저장되어 있음
                    setOpenMonthlyCalendar(false);
                  }}
                  // SplitRowQuarter로 '닫기 / 저장' 버튼 구성
                  renderButton={({ onClick, disabled }) => (
                    <SplitRowQuarter
                      leftText="닫기"
                      leftType="empty"
                      onLeft={() => setOpenMonthlyCalendar(false)}
                      rightText="저장"
                      onRight={onClick}      // BottomWithButton의 onSubmit 호출
                      rightDisabled={disabled}
                    />
                  )}
                >
                  <div className="pb-[8px]">
                    <div className="rounded-[20px] border border-border-default">

                      {/* 1~31일 버튼 */}
                      <div className="grid grid-cols-7">
                        {DAYS.map((day) => {
                          const dayStr = String(day);
                          const selected = selectedDates.includes(dayStr);
                          const inRange = isDayEnabled(day);
                          const hasReachedMax =
                            maxMonthlyCount && selectedDates.length >= maxMonthlyCount;
                          const enabled =
                            inRange && (!hasReachedMax || selected);

                          const handleClick = () => {
                            if (!enabled) return;

                            setSelectedDates((prev) => {
                              // 이미 선택 → 해제
                              if (prev.includes(dayStr)) {
                                return prev.filter((d) => d !== dayStr);
                              }

                              // 새로 선택
                              if (maxMonthlyCount && prev.length >= maxMonthlyCount) {
                                return prev; // 여기까지 오면 사실상 안 들어오지만 안전하게
                              }

                              const next = [...prev, dayStr];
                              return next.sort((a, b) => Number(a) - Number(b));
                            });
                          };

                          return (
                            <div key={day} className="h-[52px] justify-center items-center flex">
                              <button
                                type="button"
                                onClick={handleClick}
                                disabled={!enabled}
                                className={[
                                  "mx-auto flex items-center justify-center",
                                  "w-[32px] h-[32px] rounded-full text-c3",
                                  !enabled
                                    ? "text-text-disabled cursor-not-allowed"
                                    : selected
                                      ? "bg-surface-light-primary-light text-text-primary"
                                      : "text-text-default",
                                ].join(" ")}
                              >
                                {day}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </BottomWithButton>
              )}
            </div>

            {/* dimmed layer: 알림 반복 ~ 아래 내용만 가림 (footer는 X) */}
            {isRangeSelecting && (
              <div className="absolute inset-0 bg-bg-white opacity-80 z-5" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reserve2;
